import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarBlank } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * DateField — campo de fecha robusto en móviles (iOS/Android):
 *
 *  - Primario: <input type="date"> nativo. En iOS abre el picker de ruedas
 *    del sistema; en Android abre el date-picker Material; en desktop
 *    dispara el calendario del navegador. Es el modo más confiable en
 *    Safari iOS, que a veces bloquea Popovers de Radix dentro de Dialogs.
 *
 *  - Secundario: botón con Popover + <Calendar> (react-day-picker), útil
 *    en desktop para saltar rápido entre meses/años vía dropdowns. En
 *    móvil también funciona pero el nativo suele preferirse.
 *
 * Firma pública sin cambios: (value, onChange, testid, disabled, min, max).
 */
export const DateField = ({ value, onChange, testid, disabled = false, min, max }) => {
  const [open, setOpen] = useState(false);

  const fromMonth = min ? parseISO(min) : new Date(2000, 0);
  const toMonth = max ? parseISO(max) : new Date(new Date().getFullYear() + 5, 11);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex gap-1 items-center">
        <Input
          type="date"
          data-testid={testid ? `${testid}-input` : undefined}
          disabled={disabled}
          value={value || ""}
          min={min || undefined}
          max={max || undefined}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 text-sm flex-1 min-w-0"
        />
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-testid={testid}
            disabled={disabled}
            className="h-9 w-9 p-0 shrink-0"
            aria-label="Abrir calendario"
          >
            <CalendarBlank size={16} />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          fromMonth={fromMonth}
          toMonth={toMonth}
          selected={value && isValid(parseISO(value)) ? parseISO(value) : undefined}
          defaultMonth={value && isValid(parseISO(value)) ? parseISO(value) : undefined}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};
